import { getCSRFToken } from "./cookies";

const Client = Object.freeze({
  BROWSER: "browser",
} as const);

interface Rules {
  client: (typeof Client)[keyof typeof Client];
  baseUrl: string;
  withCredentials: boolean;
}

const rules: Rules = {
  client: Client.BROWSER,
  baseUrl: `/api/_allauth/${Client.BROWSER}/v1`, // Match Django's URL pattern from urls.py
  withCredentials: true, // Enable credentials for cross-origin requests
};

const ACCEPT_JSON = {
  accept: "application/json",
};

export const AuthProcess = Object.freeze({
  LOGIN: "login",
  CONNECT: "connect",
} as const);

type AuthProcessType = (typeof AuthProcess)[keyof typeof AuthProcess];

const URLs = Object.freeze({
  CONFIG: "/config",
  SESSION: "/auth/session",
  REDIRECT_TO_PROVIDER: "/auth/provider/redirect",
  PROVIDER_TOKEN: "/auth/provider/token",
} as const);

interface FormData {
  [key: string]: string;
}

function postForm(action: string, data: FormData): void {
  const f = document.createElement("form");
  f.method = "POST";
  f.action = rules.baseUrl + action;

  for (const key in data) {
    const d = document.createElement("input");
    d.type = "hidden";
    d.name = key;
    d.value = data[key];
    f.appendChild(d);
  }
  document.body.appendChild(f);
  f.submit();
}

const tokenStorage = window.sessionStorage;

interface RequestOptions extends RequestInit {
  credentials?: RequestCredentials;
  headers: Record<string, string>;
}

interface ResponseMeta {
  session_token?: string;
  is_authenticated?: boolean;
}

interface ApiResponse {
  status: number;
  meta?: ResponseMeta;
  [key: string]: any;
}

async function request(
  method: string,
  path: string,
  data?: any,
  headers: Record<string, string> = {},
): Promise<ApiResponse> {
  const options: RequestOptions = {
    method,
    headers: {
      ...ACCEPT_JSON,
      ...headers,
    },
  };

  if (rules.withCredentials) {
    options.credentials = "include";
  }

  // Don't pass along authentication related headers to the config endpoint.
  if (path !== URLs.CONFIG) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      options.headers["X-CSRFToken"] = csrfToken;
    }
  }

  if (typeof data !== "undefined") {
    options.body = JSON.stringify(data);
    options.headers["Content-Type"] = "application/json";
  }

  let msg: ApiResponse;
  try {
    const resp = await fetch(rules.baseUrl + path, options);
    if (!resp.ok) {
      throw new Error(`HTTP error! status: ${resp.status}`);
    }
    msg = await resp.json();
  } catch (error) {
    throw new Error("Failed to fetch or parse response");
  }

  if (msg.status === 410) {
    tokenStorage.removeItem("sessionToken");
  }

  if (msg.meta?.session_token) {
    tokenStorage.setItem("sessionToken", msg.meta.session_token);
  }

  if (
    [401, 410].includes(msg.status) ||
    (msg.status === 200 && msg.meta?.is_authenticated)
  ) {
    const event = new CustomEvent("allauth.auth.change", { detail: msg });
    document.dispatchEvent(event);
  }

  return msg;
}

export async function logout(): Promise<ApiResponse> {
  return await request("DELETE", URLs.SESSION);
}

export async function getConfig(): Promise<ApiResponse> {
  return await request("GET", URLs.CONFIG);
}

export async function getAuth(): Promise<ApiResponse> {
  return await request("GET", URLs.SESSION);
}

export async function authenticateByToken(
  providerId: string,
  token: string,
  process: AuthProcessType = AuthProcess.LOGIN,
): Promise<ApiResponse> {
  return await request("POST", URLs.PROVIDER_TOKEN, {
    provider: providerId,
    token,
    process,
  });
}

export function redirectToProvider(
  providerId: string,
  callbackURL: string,
  process: AuthProcessType = AuthProcess.LOGIN,
): void {
  postForm(URLs.REDIRECT_TO_PROVIDER, {
    provider: providerId,
    process,
    callback_url:
      window.location.protocol + "//" + window.location.host + callbackURL,
    csrfmiddlewaretoken: getCSRFToken() as string,
  });
}
